// apps/admin-dashboard/src/pages/MemberFormPage.tsx
import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Images } from 'lucide-react';
import type { Member, MemberType, LeadershipRole, ActivityStatus, MemberPosition, SocialLink } from '@yp/types';
import { useAuth } from '../context/AuthContext';
import { getAdminMembers, createMember, updateMember } from '../lib/api';
import ImagePickerModal from '../components/ImagePickerModal';

const MEMBER_TYPES: MemberType[] = ['member', 'trainee'];
const LEADERSHIP_ROLES: Array<LeadershipRole | ''> = ['', 'leader', 'co-leader'];
const ACTIVITY_STATUSES: ActivityStatus[] = ['active', 'graduated', 'hiatus'];
const BLOOD_TYPES = ['A', 'B', 'AB', 'O'] as const;
const POSITIONS: MemberPosition[] = [
  'main_vocalist','lead_vocalist','vocalist',
  'main_dancer','lead_dancer','dancer',
  'main_rapper','lead_rapper','rapper',
  'visual','center','maknae',
];
const SOCIAL_PLATFORMS = ['instagram','twitter','tiktok','showroom','idn','youtube'];

function toSlug(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const DEFAULT_FORM: Partial<Member> = {
  nickname: '',
  full_name: '',
  slug: '',
  member_type: 'member',
  leadership_role: null,
  activity_status: 'active',
  graduated_at: null,
  birth_date: null,
  height: null,
  blood_type: null,
  positions: [],
  profile_photo: null,
  cover_photo: null,
  jikoshoukai: null,
  socials_json: [],
  order_index: 0,
};

export default function MemberFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [form, setForm] = useState<Partial<Member>>(DEFAULT_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEdit);
  const [error, setError] = useState('');
  const [profilePickerOpen, setProfilePickerOpen] = useState(false);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);

  useEffect(() => {
    if (!isEdit || !accessToken || !id) return;
    getAdminMembers(accessToken)
      .then(({ data }) => {
        const found = data.find((m) => m.id === id);
        if (found) setForm(found);
      })
      .finally(() => setIsFetching(false));
  }, [isEdit, accessToken, id]);

  function set<K extends keyof Member>(key: K, value: Member[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function togglePosition(pos: MemberPosition) {
    setForm((prev) => {
      const positions = prev.positions ?? [];
      return {
        ...prev,
        positions: positions.includes(pos)
          ? positions.filter((p) => p !== pos)
          : [...positions, pos],
      };
    });
  }

  function updateSocial(index: number, field: keyof SocialLink, value: string) {
    setForm((prev) => {
      const socials = [...(prev.socials_json ?? [])];
      socials[index] = { ...socials[index], [field]: value } as SocialLink;
      return { ...prev, socials_json: socials };
    });
  }

  function addSocial() {
    setForm((prev) => ({
      ...prev,
      socials_json: [...(prev.socials_json ?? []), { platform: '', handle: '', url: '' }],
    }));
  }

  function removeSocial(index: number) {
    setForm((prev) => ({
      ...prev,
      socials_json: (prev.socials_json ?? []).filter((_, i) => i !== index),
    }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!accessToken) return;
    setError('');
    setIsLoading(true);
    try {
      if (isEdit && id) {
        await updateMember(accessToken, id, form);
      } else {
        await createMember(accessToken, form);
      }
      navigate('/members');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  }

  const inputCls = 'w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none';
  const labelCls = 'mb-1.5 block text-xs font-medium text-gray-400';

  if (isFetching) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-pink-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/members')}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-white/5 hover:text-gray-200"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-gray-100">
          {isEdit ? 'Edit Member' : 'Add Member'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photos */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Profile Photo</label>
            <div className="flex flex-col gap-2">
              {form.profile_photo && (
                <img src={form.profile_photo} alt="profile" className="h-24 w-20 rounded-lg object-cover" />
              )}
              <button type="button" onClick={() => setProfilePickerOpen(true)}
                className="flex items-center gap-1 rounded-lg border border-dashed border-[var(--color-border)] px-3 py-2 text-xs text-gray-500 hover:border-pink-500/30 hover:text-pink-400">
                <Images size={14} /> Pilih
              </button>
            </div>
          </div>
          <div>
            <label className={labelCls}>Cover Photo</label>
            <div className="flex flex-col gap-2">
              {form.cover_photo && (
                <img src={form.cover_photo} alt="cover" className="h-24 w-20 rounded-lg object-cover" />
              )}
              <button type="button" onClick={() => setCoverPickerOpen(true)}
                className="flex items-center gap-1 rounded-lg border border-dashed border-[var(--color-border)] px-3 py-2 text-xs text-gray-500 hover:border-pink-500/30 hover:text-pink-400">
                <Images size={14} /> Pilih
              </button>
            </div>
          </div>
        </div>

        {/* Nickname + slug */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Nickname / Stage Name <span className="text-red-400">*</span></label>
            <input type="text" required value={form.nickname ?? ''}
              onChange={(e) => {
                const nickname = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  nickname,
                  slug: isEdit ? prev.slug : toSlug(nickname),
                }));
              }}
              className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Slug <span className="text-red-400">*</span></label>
            <input type="text" required value={form.slug ?? ''}
              onChange={(e) => set('slug', e.target.value)}
              className={inputCls} />
          </div>
        </div>

        {/* Full name */}
        <div>
          <label className={labelCls}>Full Name (nama asli)</label>
          <input type="text" value={form.full_name ?? ''}
            onChange={(e) => set('full_name', e.target.value || null)}
            className={inputCls} />
        </div>

        {/* Member type + leadership role */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Member Type</label>
            <select value={form.member_type ?? 'member'}
              onChange={(e) => {
                const member_type = e.target.value as MemberType;
                setForm((prev) => ({ ...prev, member_type, leadership_role: null }));
              }}
              className={`${inputCls} bg-[var(--color-surface)]`}>
              {MEMBER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {form.member_type === 'member' && (
            <div>
              <label className={labelCls}>Leadership Role</label>
              <select value={form.leadership_role ?? ''}
                onChange={(e) => set('leadership_role', (e.target.value as LeadershipRole) || null)}
                className={`${inputCls} bg-[var(--color-surface)]`}>
                {LEADERSHIP_ROLES.map((r) => (
                  <option key={r} value={r}>{r === '' ? '— none —' : r}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Activity status + graduated_at */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Activity Status</label>
            <select value={form.activity_status ?? 'active'}
              onChange={(e) => set('activity_status', e.target.value as ActivityStatus)}
              className={`${inputCls} bg-[var(--color-surface)]`}>
              {ACTIVITY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {form.activity_status === 'graduated' && (
            <div>
              <label className={labelCls}>Graduated At</label>
              <input type="date" value={form.graduated_at?.slice(0, 10) ?? ''}
                onChange={(e) => set('graduated_at', e.target.value ? new Date(e.target.value).toISOString() : null)}
                className={inputCls} />
            </div>
          )}
        </div>

        {/* Birth date + height + blood type */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Birth Date</label>
            <input type="date" value={form.birth_date ?? ''}
              onChange={(e) => set('birth_date', e.target.value || null)}
              className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Height (cm)</label>
            <input type="number" min={100} max={220} value={form.height ?? ''}
              onChange={(e) => set('height', e.target.value ? Number(e.target.value) : null)}
              className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Blood Type</label>
            <select value={form.blood_type ?? ''}
              onChange={(e) => set('blood_type', (e.target.value as Member['blood_type']) || null)}
              className={`${inputCls} bg-[var(--color-surface)]`}>
              <option value="">—</option>
              {BLOOD_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        </div>

        {/* Jikoshoukai */}
        <div>
          <label className={labelCls}>Jikoshoukai (self-introduction)</label>
          <textarea rows={3} value={form.jikoshoukai ?? ''}
            onChange={(e) => set('jikoshoukai', e.target.value || null)}
            className={`${inputCls} resize-none`} />
        </div>

        {/* Positions */}
        <div>
          <label className={labelCls}>Positions</label>
          <div className="flex flex-wrap gap-2">
            {POSITIONS.map((pos) => {
              const selected = (form.positions ?? []).includes(pos);
              return (
                <button key={pos} type="button" onClick={() => togglePosition(pos)}
                  className={`rounded-full px-3 py-1 text-xs transition-colors ${
                    selected ? 'bg-pink-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}>
                  {pos.replace(/_/g, ' ')}
                </button>
              );
            })}
          </div>
        </div>

        {/* Order index */}
        <div>
          <label className={labelCls}>Order Index</label>
          <input type="number" min={0} value={form.order_index ?? 0}
            onChange={(e) => set('order_index', Number(e.target.value))}
            className={`${inputCls} w-24`} />
        </div>

        {/* Socials */}
        <div>
          <label className={labelCls}>Social Media</label>
          <div className="space-y-2">
            {(form.socials_json ?? []).map((s, i) => (
              <div key={i} className="flex gap-2 items-center">
                <select value={s.platform}
                  onChange={(e) => updateSocial(i, 'platform', e.target.value)}
                  className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5 text-xs text-gray-300 focus:outline-none w-28">
                  <option value="">platform</option>
                  {SOCIAL_PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <input type="text" placeholder="@handle" value={s.handle ?? ''}
                  onChange={(e) => updateSocial(i, 'handle', e.target.value)}
                  className="rounded-lg border border-[var(--color-border)] bg-black/20 px-2 py-1.5 text-xs text-gray-300 focus:outline-none w-28" />
                <input type="url" placeholder="https://..." value={s.url}
                  onChange={(e) => updateSocial(i, 'url', e.target.value)}
                  className="flex-1 rounded-lg border border-[var(--color-border)] bg-black/20 px-2 py-1.5 text-xs text-gray-300 focus:outline-none" />
                <button type="button" onClick={() => removeSocial(i)}
                  className="text-xs text-gray-500 hover:text-red-400">✕</button>
              </div>
            ))}
            <button type="button" onClick={addSocial}
              className="text-xs text-pink-400 hover:underline">+ Add social</button>
          </div>
        </div>

        {error && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={() => navigate('/members')}
            className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-gray-400 hover:bg-white/5">
            Cancel
          </button>
          <button type="submit" disabled={isLoading}
            className="rounded-lg px-6 py-2 text-sm font-medium text-white disabled:opacity-60"
            style={{ backgroundColor: 'var(--yp-pink)' }}>
            {isLoading ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Member'}
          </button>
        </div>
      </form>

      <ImagePickerModal open={profilePickerOpen} onClose={() => setProfilePickerOpen(false)}
        onSelect={(url) => set('profile_photo', url)} defaultFolder="members" />
      <ImagePickerModal open={coverPickerOpen} onClose={() => setCoverPickerOpen(false)}
        onSelect={(url) => set('cover_photo', url)} defaultFolder="members" />
    </div>
  );
}
